/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import React, { useEffect, useState, useCallback } from 'react';
// @mui
import {
    Stack,
    Box,
    Button,
    CircularProgress,
    Typography,
    Checkbox,
    Chip,
    Divider,
    Tooltip,
    IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
// components
import { Iconify } from '../../../components';
// redux
// services
import { callApi } from '../../../utils/apiCall';
import { gmbListAccounts, gmbListLocations } from '../../../services/googleMyBusiness';
// hooks
import useSnackbar from '../../../hooks/useCustomSnackbar';
import useDirtyForm from '../../../hooks/useDirtyForm';
// constants
import { days } from '../../../constants/openHours';
import {
    HOUR_PRESETS,
    DAY_GROUPS,
    normalizeToTimeBlocks,
    timeBlocksToLegacy,
} from '../../../constants/openHourPresets';
// components/settings
import StickySaveBar from '../../../components/settings/StickySaveBar';

// ─── helpers ──────────────────────────────────────────────────────────────────

/** Convert "HH:mm" or "HH:mm:ss" string to a native Date (for date-fns adapter) */
function timeStrToDate(str) {
    if (!str) return null;
    const parts = str.split(':');
    const d = new Date();
    d.setHours(Number(parts[0]) || 0, Number(parts[1]) || 0, Number(parts[2]) || 0, 0);
    return d;
}

/** Convert a Date object → "HH:mm:ss" string */
function dateToStr(d) {
    if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return null;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
}

/** How long is the break between two consecutive time_blocks (in minutes) */
function breakDuration(block1, block2) {
    if (!block1 || !block2) return null;
    const end = timeStrToDate(block1.end_time);
    const start = timeStrToDate(block2.start_time);
    if (!end || !start) return null;
    return Math.round((start.getTime() - end.getTime()) / 60000);
}

/**
 * Build per-day state from the raw API hoursList.
 * Returns an array matching the shape of `days`.
 */
function buildDayState(hoursList) {
    return days.map((d) => {
        const record = hoursList.find((h) => h.day === d.day);
        const blocks = record ? normalizeToTimeBlocks(record) : [];
        return {
            day: d.day,
            id: record?.id ?? null,
            time_blocks: blocks,          // [{ start_time, end_time }, ...]
        };
    });
}

/** Day label display */
const DAY_LABELS = {
    MONDAY: 'Monday',
    TUESDAY: 'Tuesday',
    WEDNESDAY: 'Wednesday',
    THURSDAY: 'Thursday',
    FRIDAY: 'Friday',
    SATURDAY: 'Saturday',
    SUNDAY: 'Sunday',
};

// ─── status helpers ────────────────────────────────────────────────────────────

function getDayStatus(dayObj) {
    // eslint-disable-next-line camelcase
    const { time_blocks } = dayObj;
    // eslint-disable-next-line camelcase
    if (!time_blocks || time_blocks.length === 0) return 'closed';
    // eslint-disable-next-line camelcase
    if (time_blocks.length > 1) return 'split';
    return 'open';
}

function StatusDot({ status }) {
    const colors = { open: '#22c55e', split: '#f59e0b', closed: '#ef4444' };
    return (
        <Box
            sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: colors[status] || colors.closed,
                flexShrink: 0,
            }}
        />
    );
}
StatusDot.propTypes = { status: PropTypes.string.isRequired };

// ─── TimeBlockRow ──────────────────────────────────────────────────────────────

function TimeBlockRow({ block, blockIndex, totalBlocks, onChangeStart, onChangeEnd, disabled }) {
    const startVal = timeStrToDate(block.start_time);
    const endVal = timeStrToDate(block.end_time);

    let label = 'Opens';
    if (blockIndex === 0 && totalBlocks > 1) label = 'Morning';
    else if (blockIndex === 1) label = 'Afternoon';

    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
            <TimePicker
                label={`${label} — Opens`}
                value={startVal}
                onChange={(val) => onChangeStart(val)}
                disabled={disabled}
                slotProps={{ textField: { size: 'small', sx: { width: 140 }, onKeyDown: (e) => e.preventDefault() } }}
            />
            <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>–</Typography>
            <TimePicker
                label="Closes"
                value={endVal}
                onChange={(val) => onChangeEnd(val)}
                disabled={disabled}
                slotProps={{ textField: { size: 'small', sx: { width: 140 }, onKeyDown: (e) => e.preventDefault() } }}
            />
        </Stack>
    );
}
TimeBlockRow.propTypes = {
    block: PropTypes.shape({ start_time: PropTypes.string, end_time: PropTypes.string }).isRequired,
    blockIndex: PropTypes.number.isRequired,
    totalBlocks: PropTypes.number.isRequired,
    onChangeStart: PropTypes.func.isRequired,
    onChangeEnd: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};
TimeBlockRow.defaultProps = {
    disabled: false,
};

// ─── main component ────────────────────────────────────────────────────────────

export default function OpenHour({ hoursList, practiceId, canEdit }) {
    const [hoursData, setHoursData] = useState(() => buildDayState([]));
    const [selectedDays, setSelectedDays] = useState([]);
    const [syncLoading, setSyncLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isGoogleConnected, setIsGoogleConnected] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { isDirty, markDirty, markClean, resetTo } = useDirtyForm({});

    // Warn user on page leave when there are unsaved changes
    useEffect(() => {
        const handler = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    // Load / normalise from prop
    useEffect(() => {
        if (!hoursList) return;
        const state = buildDayState(hoursList);
        setHoursData(state);
        resetTo(state);
    }, [hoursList]);

    // Google connection check
    useEffect(() => {
        if (!practiceId) return undefined;
        let cancelled = false;
        gmbListAccounts(practiceId)
            .then((resp) => {
                if (!cancelled) {
                    const accounts = Array.isArray(resp) ? resp : resp?.results || [];
                    setIsGoogleConnected(accounts.length > 0);
                }
            })
            .catch(() => { if (!cancelled) setIsGoogleConnected(false); });
        return () => { cancelled = true; };
    }, [practiceId]);

    // ── mutation helpers ──

    const updateDay = useCallback((dayIndex, updater) => {
        setHoursData((prev) => {
            const next = prev.map((d, i) => (i === dayIndex ? updater(d) : d));
            return next;
        });
        markDirty();
    }, [markDirty]);

    const handleBlockChange = (dayIndex, blockIndex, field, dateVal) => {
        updateDay(dayIndex, (d) => {
            const blocks = d.time_blocks.map((b, bi) =>
                bi === blockIndex ? { ...b, [field]: dateVal ? dateToStr(dateVal) : b[field] } : b
            );
            return { ...d, time_blocks: blocks };
        });
    };

    const closeDay = (dayIndex) => {
        updateDay(dayIndex, (d) => ({ ...d, time_blocks: [] }));
    };

    const addBreak = (dayIndex) => {
        updateDay(dayIndex, (d) => {
            const [first] = d.time_blocks;
            if (!first) return d;
            const startDate = timeStrToDate(first.start_time);
            const endDate = timeStrToDate(first.end_time);
            const totalMs = endDate.getTime() - startDate.getTime();
            const midMs = startDate.getTime() + totalMs / 2;
            const halfBreak = 30 * 60 * 1000; // 30 min each side = 1 hour break
            const breakStart = new Date(midMs - halfBreak);
            const breakEnd = new Date(midMs + halfBreak);
            return {
                ...d,
                time_blocks: [
                    { start_time: first.start_time, end_time: dateToStr(breakStart) },
                    { start_time: dateToStr(breakEnd), end_time: first.end_time },
                ],
            };
        });
    };

    const removeBreak = (dayIndex) => {
        updateDay(dayIndex, (d) => {
            if (d.time_blocks.length < 2) return d;
            return {
                ...d,
                time_blocks: [{ start_time: d.time_blocks[0].start_time, end_time: d.time_blocks[d.time_blocks.length - 1].end_time }],
            };
        });
    };

    // ── day selection ──

    const toggleDaySelect = (day) => {
        setSelectedDays((prev) =>
            prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
        );
    };

    const selectGroup = (groupDays) => {
        setSelectedDays((prev) => {
            const allSelected = groupDays.every((d) => prev.includes(d));
            if (allSelected) return prev.filter((d) => !groupDays.includes(d));
            return [...new Set([...prev, ...groupDays])];
        });
    };

    // ── bulk actions ──

    const bulkClose = () => {
        if (selectedDays.length === 0) return;
        setHoursData((prev) =>
            prev.map((d) => selectedDays.includes(d.day) ? { ...d, time_blocks: [] } : d)
        );
        markDirty();
    };

    const bulkSetTime = (startStr, endStr) => {
        if (selectedDays.length === 0) return;
        setHoursData((prev) =>
            prev.map((d) =>
                selectedDays.includes(d.day)
                    ? { ...d, time_blocks: [{ start_time: startStr, end_time: endStr }] }
                    : d
            )
        );
        markDirty();
    };

    const copyToSelected = (sourceDayIndex) => {
        if (selectedDays.length === 0) return;
        const source = hoursData[sourceDayIndex];
        setHoursData((prev) =>
            prev.map((d) =>
                selectedDays.includes(d.day)
                    ? { ...d, time_blocks: source.time_blocks.map((b) => ({ ...b })) }
                    : d
            )
        );
        markDirty();
    };

    // ── preset apply ──

    const applyPreset = (preset) => {
        setHoursData((prev) =>
            prev.map((d) => ({
                ...d,
                time_blocks: preset.days[d.day] ? preset.days[d.day].map((b) => ({ ...b })) : [],
            }))
        );
        markDirty();
    };

    // ── save ──

    // Backend currently supports single start_time/end_time per day only.
    // Split shifts (time_blocks with >1 block) are stored locally but saved
    // as the full span (first start → last end) until backend adds time_blocks support.
    const buildPayload = (data) =>
        data
            .filter((d) => d.time_blocks && d.time_blocks.length > 0)
            .map((d) => {
                const legacy = timeBlocksToLegacy(d.time_blocks);
                const item = {
                    day: d.day,
                    start_time: legacy.start_time,
                    end_time: legacy.end_time,
                };
                if (d.id) item.id = d.id;
                return item;
            });

    const onSave = async () => {
        setSaving(true);
        try {
            const payload = buildPayload(hoursData);
            const resp = await callApi(`/api/v1/practices/${practiceId}/`, 'PATCH', { opening_hours: payload });
            if (!resp.ok) throw new Error(resp.error || 'Failed to save opening hours');
            enqueueSnackbar('Opening hours saved', { variant: 'success' });
            markClean();
        } catch (err) {
            enqueueSnackbar(err?.message || 'Failed to save opening hours', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const onDiscard = () => {
        if (hoursList) {
            setHoursData(buildDayState(hoursList));
        }
        markClean();
    };

    // ── Google sync ──

    const handleSyncFromGoogle = async () => {
        if (!practiceId) return;
        setSyncLoading(true);
        try {
            const accountsResp = await gmbListAccounts(practiceId);
            const accounts = Array.isArray(accountsResp) ? accountsResp : accountsResp?.results || [];
            if (accounts.length === 0) {
                enqueueSnackbar('No Google Business account connected. Connect one in Integrations.', { variant: 'warning', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
                return;
            }
            const locationsResp = await gmbListLocations(practiceId, accounts[0].id, { page_size: 100 });
            const locations = Array.isArray(locationsResp) ? locationsResp : locationsResp?.results || locationsResp?.locations || [];
            const connected = locations.filter((loc) => loc?.is_connected !== false);
            if (connected.length === 0) {
                enqueueSnackbar('No Google Business location connected.', { variant: 'warning', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
                return;
            }
            const ld = connected[0]?.location_data || connected[0] || {};
            const periods = Array.isArray(ld.regularHours?.periods) ? ld.regularHours.periods : [];
            if (periods.length === 0) {
                enqueueSnackbar('No opening hours found in Google Business profile.', { variant: 'warning', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
                return;
            }
            const fmtTime = (t) => {
                if (!t) return null;
                return `${String(t.hours ?? 0).padStart(2, '0')}:${String(t.minutes ?? 0).padStart(2, '0')}`;
            };
            setHoursData((prev) =>
                prev.map((d) => {
                    const period = periods.find((p) => (p.openDay || p.closeDay) === d.day);
                    if (period) {
                        const startStr = fmtTime(period.openTime);
                        const endStr = fmtTime(period.closeTime);
                        return {
                            ...d,
                            time_blocks: startStr && endStr ? [{ start_time: startStr, end_time: endStr }] : [],
                        };
                    }
                    return { ...d, time_blocks: [] };
                })
            );
            markDirty();
            enqueueSnackbar('Opening hours synced from Google Business. Review and save.', { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
        } catch (err) {
            enqueueSnackbar(err?.message || 'Failed to sync from Google Business', { variant: 'error' });
        } finally {
            setSyncLoading(false);
        }
    };

    // ─── hours summary ─────────────────────────────────────────────────────────

    const hoursSummary = (() => {
        const DAY_SHORT = { MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed', THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat', SUNDAY: 'Sun' };
        const DAY_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

        const formatTime = (str) => {
            if (!str) return '';
            const parts = str.split(':');
            const h = Number(parts[0]);
            const m = Number(parts[1] || 0);
            return m === 0 ? `${h}:00` : `${h}:${String(m).padStart(2, '0')}`;
        };

        const blockKey = (blocks) => {
            if (!blocks || blocks.length === 0) return 'CLOSED';
            return blocks.map((b) => `${b.start_time}-${b.end_time}`).join('|');
        };

        // Group consecutive days with the same block pattern
        const groups = [];
        let current = null;
        DAY_ORDER.forEach((day) => {
            const dayObj = hoursData.find((d) => d.day === day);
            const key = dayObj ? blockKey(dayObj.time_blocks) : 'CLOSED';
            const blocks = dayObj?.time_blocks || [];
            if (current && current.key === key) {
                current.days.push(day);
            } else {
                current = { key, days: [day], blocks };
                groups.push(current);
            }
        });

        return groups.map((g) => {
            const first = g.days[0];
            const last = g.days[g.days.length - 1];
            const dayLabel = first === last ? DAY_SHORT[first] : `${DAY_SHORT[first]}–${DAY_SHORT[last]}`;
            if (g.key === 'CLOSED') return `${dayLabel} Closed`;
            const timeLabel = g.blocks.map((b) => `${formatTime(b.start_time)}–${formatTime(b.end_time)}`).join(', ');
            return `${dayLabel} ${timeLabel}`;
        }).join(' · ');
    })();

    // ─── render ────────────────────────────────────────────────────────────────

    const anySelected = selectedDays.length > 0;

    return (
        <>
            {/* Hours summary */}
            {hoursSummary && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                    {hoursSummary}
                </Typography>
            )}

            {/* Quick Presets */}
            {canEdit && (
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                    Quick Presets
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {HOUR_PRESETS.map((preset) => (
                        <Chip
                            key={preset.label}
                            label={preset.label}
                            size="small"
                            onClick={() => applyPreset(preset)}
                            sx={{
                                borderRadius: '8px',
                                fontSize: 12,
                                fontWeight: 500,
                                cursor: 'pointer',
                                bgcolor: '#f1f5f9',
                                color: '#475569',
                                '&:hover': { bgcolor: '#e2e8f0' },
                            }}
                        />
                    ))}
                </Stack>
            </Box>
            )}

            {/* Group selectors + bulk actions */}
            {canEdit && <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2,
                    p: 1.5,
                    bgcolor: '#f8fafc',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                }}
            >
                {DAY_GROUPS.map((group) => (
                    <Chip
                        key={group.label}
                        label={group.label}
                        size="small"
                        onClick={() => selectGroup(group.days)}
                        sx={{
                            borderRadius: '8px',
                            fontSize: 12,
                            fontWeight: 500,
                            cursor: 'pointer',
                            bgcolor: group.days.every((d) => selectedDays.includes(d)) ? alpha('#06b6d4', 0.12) : '#fff',
                            color: group.days.every((d) => selectedDays.includes(d)) ? '#0891b2' : '#475569',
                            border: '1px solid',
                            borderColor: group.days.every((d) => selectedDays.includes(d)) ? alpha('#06b6d4', 0.3) : '#e2e8f0',
                            '&:hover': { bgcolor: alpha('#06b6d4', 0.08) },
                        }}
                    />
                ))}

                {anySelected && (
                    <>
                        <Box sx={{ flex: 1 }} />
                        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                            {selectedDays.length} selected —
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => bulkSetTime('09:00:00', '17:30:00')}
                            sx={{ fontSize: 12, textTransform: 'none', borderRadius: '8px', py: 0.5 }}
                        >
                            Set 9–17:30
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={bulkClose}
                            sx={{ fontSize: 12, textTransform: 'none', borderRadius: '8px', py: 0.5 }}
                        >
                            Close
                        </Button>
                    </>
                )}
            </Box>}

            {/* Day rows */}
            <Stack spacing={1}>
                {hoursData.map((dayObj, dayIndex) => {
                    const status = getDayStatus(dayObj);
                    const isSelected = selectedDays.includes(dayObj.day);

                    return (
                        <Box
                            key={dayObj.day}
                            sx={{
                                border: '1px solid',
                                borderColor: isSelected ? alpha('#06b6d4', 0.35) : '#e2e8f0',
                                borderRadius: '12px',
                                p: 1.5,
                                bgcolor: isSelected ? alpha('#06b6d4', 0.03) : '#fff',
                                transition: 'all 0.15s',
                            }}
                        >
                            {/* Row header */}
                            <Stack direction="row" alignItems="center" spacing={1.5} mb={status !== 'closed' ? 1.5 : 0}>
                                <Checkbox
                                    size="small"
                                    checked={isSelected}
                                    onChange={() => canEdit && toggleDaySelect(dayObj.day)}
                                    disabled={!canEdit}
                                    sx={{ p: 0.25 }}
                                />
                                <StatusDot status={status} />
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: '#334155',
                                        minWidth: 90,
                                    }}
                                >
                                    {DAY_LABELS[dayObj.day]}
                                </Typography>

                                {status === 'closed' && (
                                    <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                                        Closed
                                    </Typography>
                                )}

                                <Box sx={{ flex: 1 }} />

                                {canEdit && (
                                    <>
                                {/* Copy to selected */}
                                {anySelected && status !== 'closed' && (
                                    <Tooltip title={`Copy ${DAY_LABELS[dayObj.day]} hours to selected days`}>
                                        <IconButton
                                            size="small"
                                            onClick={() => copyToSelected(dayIndex)}
                                            sx={{ color: '#64748b', '&:hover': { color: '#0891b2' } }}
                                        >
                                            <Iconify icon="solar:copy-bold" width={16} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {/* Add break / Remove break */}
                                {status === 'open' && (
                                    <Tooltip title="Split into morning & afternoon (add lunch break)">
                                        <Button
                                            size="small"
                                            onClick={() => addBreak(dayIndex)}
                                            startIcon={<Iconify icon="solar:add-circle-bold" width={14} />}
                                            sx={{ fontSize: 11, textTransform: 'none', color: '#64748b', '&:hover': { color: '#0891b2' }, py: 0.25, minWidth: 0 }}
                                        >
                                            Add break
                                        </Button>
                                    </Tooltip>
                                )}
                                {status === 'split' && (
                                    <Tooltip title="Remove lunch break, merge back to single block">
                                        <Button
                                            size="small"
                                            onClick={() => removeBreak(dayIndex)}
                                            startIcon={<Iconify icon="solar:minus-circle-bold" width={14} />}
                                            sx={{ fontSize: 11, textTransform: 'none', color: '#f59e0b', py: 0.25, minWidth: 0 }}
                                        >
                                            Remove break
                                        </Button>
                                    </Tooltip>
                                )}

                                {/* Close / reopen */}
                                {status !== 'closed' && (
                                    <Tooltip title="Mark as closed">
                                        <IconButton
                                            size="small"
                                            onClick={() => closeDay(dayIndex)}
                                            sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                        >
                                            <Iconify icon="solar:trash-bin-trash-bold" width={16} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {status === 'closed' && (
                                    <Tooltip title="Set as open (9:00–17:30)">
                                        <IconButton
                                            size="small"
                                            onClick={() => updateDay(dayIndex, (d) => ({ ...d, time_blocks: [{ start_time: '09:00:00', end_time: '17:30:00' }] }))}
                                            sx={{ color: '#94a3b8', '&:hover': { color: '#22c55e' } }}
                                        >
                                            <Iconify icon="solar:add-circle-bold" width={16} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                    </>
                                )}
                            </Stack>

                            {/* Time blocks */}
                            {dayObj.time_blocks.map((block, blockIndex) => (
                                <Box key={blockIndex}>
                                    {blockIndex > 0 && (
                                        <Box sx={{ my: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Divider sx={{ flex: 1, borderStyle: 'dashed', borderColor: '#fbbf24' }} />
                                            <Typography sx={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                {(() => {
                                                    const mins = breakDuration(dayObj.time_blocks[blockIndex - 1], block);
                                                    if (mins === null) return 'Break';
                                                    if (mins < 60) return `${mins}m break`;
                                                    return `${Math.floor(mins / 60)}h${mins % 60 > 0 ? ` ${mins % 60}m` : ''} break`;
                                                })()}
                                            </Typography>
                                            <Divider sx={{ flex: 1, borderStyle: 'dashed', borderColor: '#fbbf24' }} />
                                        </Box>
                                    )}
                                    <TimeBlockRow
                                        block={block}
                                        blockIndex={blockIndex}
                                        totalBlocks={dayObj.time_blocks.length}
                                        onChangeStart={(val) => handleBlockChange(dayIndex, blockIndex, 'start_time', val)}
                                        onChangeEnd={(val) => handleBlockChange(dayIndex, blockIndex, 'end_time', val)}
                                        disabled={!canEdit}
                                    />
                                </Box>
                            ))}
                        </Box>
                    );
                })}
            </Stack>

            {/* Google sync button */}
            {canEdit && isGoogleConnected && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={handleSyncFromGoogle}
                        disabled={syncLoading}
                        startIcon={
                            syncLoading
                                ? <CircularProgress size={18} />
                                : <Iconify icon="flat-color-icons:google" width={20} height={20} />
                        }
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: '20px', py: '8px' }}
                    >
                        {syncLoading ? 'Syncing…' : 'Sync from Google'}
                    </Button>
                </Box>
            )}

            {canEdit && <StickySaveBar isDirty={isDirty} onSave={onSave} onDiscard={onDiscard} saving={saving} />}
        </>
    );
}

OpenHour.propTypes = {
    hoursList: PropTypes.array,
    practiceId: PropTypes.any,
    canEdit: PropTypes.bool,
};

OpenHour.defaultProps = {
    hoursList: [],
    practiceId: null,
    canEdit: false,
};
